/**
 * Cadastros: a fábrica, quem trabalha nela, os aparelhos e os parâmetros.
 *
 * Estas telas seguem o desenho do System Settings do macOS — linhas agrupadas
 * em cartão, rótulo à esquerda e controle à direita — porque é a forma que o
 * design system já resolve e porque cadastro é exatamente isso: uma lista de
 * decisões, uma por linha.
 *
 * Os valores que vêm de fábrica são **partida**, não verdade sobre a Gráfica
 * Boa União. Cada tela diz isso onde importa, para ninguém tomar o esqueleto
 * por levantamento.
 */

import {
  DESCRICAO_PARAMETROS,
  PAPEIS_SETOR,
  formato,
  type Calendario,
  type DiaCalendario,
  type Estacao,
  type PapelSetor,
  type Parametros,
  type Pessoa,
  type Setor,
} from '@pcp/dominio';
import { PINS_DE_PARTIDA } from '@pcp/dados';
import { aviso, botao, h, notificar, render, sf, tituloDeSecao, vazio } from '@pcp/ui';
import type { Aplicacao } from '../estado.js';
import { perguntar } from '../dialogos.js';

const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const ROTULO_PAPEL: Record<PapelSetor, string> = {
  pre_impressao: 'Pré-impressão',
  impressao: 'Impressão',
  acabamento: 'Acabamento',
  expedicao: 'Expedição',
  outro: 'Comum',
};

/* ================================================================== */
/* Fábrica: setores, estações, motivos, calendário                     */
/* ================================================================== */

export async function telaFabrica(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const c = await app.repo.carregarCadastros();

  render(destino, [
    h('.vstack.gap-4.pcp-coluna-leitura', [
      aviso(
        'info',
        'Confira esta fábrica com quem produz: quais setores e postos existem, o que falta, o que sobra. Cada setor tem um papel no fluxo — e o sistema lê o papel, não o nome.',
      ),

      h('.cartao', [
        tituloDeSecao('Setores', botao('Novo setor', () => novoSetor(app), { icone: 'plus', pequeno: true })),
        h('.vstack', { style: { gap: '1px' } },
          c.setores.map((s) => linhaSetor(app, s, c.estacoes))),
      ]),

      h('.cartao', [
        tituloDeSecao(
          'Estações',
          botao('Nova estação', () => novaEstacao(app), { icone: 'plus', pequeno: true }),
        ),
        h('table.tab', [
          h('thead', h('tr', [
            h('th', 'Estação'),
            h('th', { style: { width: '150px' } }, 'Setor'),
            h('th', { style: { width: '86px' } }, 'Código'),
            h('th', { style: { width: '110px' } }, 'Em paralelo'),
            h('th', { style: { width: '84px' } }, 'Ativa'),
          ])),
          h('tbody',
            c.estacoes.map((e) =>
              h('tr', [
                h('td', { style: { fontWeight: '500' } }, e.nome),
                h('td.t-secondary', c.setores.find((s) => s.id === e.setorId)?.nome ?? '—'),
                h('td.pcp-mono.t-subhead', e.codigo),
                h('td', h('input.campo-e', {
                  type: 'number',
                  min: '1',
                  max: '9',
                  value: String(e.capacidadeParalela),
                  style: { width: '58px' },
                  title: 'Quantas etapas podem rodar ao mesmo tempo nesta estação',
                  onchange: async (ev: Event) => {
                    const v = Math.max(1, Number((ev.target as HTMLInputElement).value) || 1);
                    await app.repo.salvarEstacao({ ...e, capacidadeParalela: v });
                    app.redesenhar();
                  },
                })),
                h('td', botaoToggle(e.ativa, async () => {
                  await app.repo.salvarEstacao({ ...e, ativa: !e.ativa });
                  app.redesenhar();
                })),
              ]),
            ),
          ),
        ]),
      ]),

      h('.cartao', [
        tituloDeSecao(
          'Motivos de pausa',
          botao('Novo motivo', () => novoMotivo(app), { icone: 'plus', pequeno: true }),
        ),
        h('.t-subhead.t-secondary', { style: { paddingBottom: '8px' } },
          'É o que o operador escolhe ao pausar. Menos motivos e mais claros valem mais que uma lista longa.'),
        h('.vstack', { style: { gap: '1px' } },
          c.motivos.map((m) =>
            h('.mac-form-row', [
              h('.label', [
                h('span.pcp-mono.t-subhead.t-tertiary', `${m.codigo} `),
                m.descricao,
                m.exigeTexto ? h('span.selo.s-cinza', { style: { marginLeft: '6px' } }, 'exige explicação') : null,
              ]),
              h('.control', botaoToggle(m.ativo, async () => {
                await app.repo.salvarMotivo({ ...m, ativo: !m.ativo });
                app.redesenhar();
              })),
            ]),
          ),
        ),
      ]),

      blocoCalendario(app, c.calendario),
    ]),
  ]);
}

function linhaSetor(app: Aplicacao, s: Setor, estacoes: Estacao[]): HTMLElement {
  const quantas = estacoes.filter((e) => e.setorId === s.id).length;
  return h('.mac-form-row', [
    h('.label.hstack.gap-2', [
      h('span', { style: { width: '11px', height: '11px', borderRadius: '3px', background: s.cor, display: 'inline-block' } }),
      s.nome,
      h('span.t-subhead.t-tertiary', `${quantas} ${quantas === 1 ? 'estação' : 'estações'}`),
    ]),
    h('.control.hstack.gap-2', [
      h('select.campo-e', {
        style: { width: '150px' },
        title: 'O papel deste setor no fluxo (é o que o sistema lê, não o nome)',
        onchange: (ev: Event) => {
          const papel = (ev.target as HTMLSelectElement).value as PapelSetor;
          void app.repo.salvarSetor({ ...s, papel }).then(() => app.redesenhar());
        },
      }, PAPEIS_SETOR.map((p) => h('option', { value: p, selected: p === s.papel }, ROTULO_PAPEL[p]))),
      botaoToggle(s.ativo, async () => {
        await app.repo.salvarSetor({ ...s, ativo: !s.ativo });
        app.redesenhar();
      }),
    ]),
  ]);
}

function blocoCalendario(app: Aplicacao, calendario: Calendario): HTMLElement {
  return h('.cartao', [
    tituloDeSecao('Horário de trabalho'),
    h('.t-subhead.t-secondary', { style: { paddingBottom: '8px' } },
      'É com este calendário que o sistema calcula atraso e "cabe até dia X". Horas fora do expediente não contam.'),
    h('.vstack', { style: { gap: '1px' } },
      calendario.dias.map((dia) => linhaDoDia(app, calendario, dia))),
  ]);
}

function linhaDoDia(app: Aplicacao, calendario: Calendario, dia: DiaCalendario): HTMLElement {
  const gravar = async (mudanca: Partial<DiaCalendario>): Promise<void> => {
    const novo: Calendario = {
      ...calendario,
      dias: calendario.dias.map((d) => (d.diaSemana === dia.diaSemana ? { ...d, ...mudanca } : d)),
    };
    await app.repo.salvarCalendario(novo);
    app.redesenhar();
  };

  const campoHora = (valor: string, chave: keyof DiaCalendario): HTMLElement =>
    h('input.campo-e', {
      type: 'time',
      value: valor,
      style: { width: '104px' },
      disabled: !dia.trabalha,
      onchange: (ev: Event) => void gravar({ [chave]: (ev.target as HTMLInputElement).value } as Partial<DiaCalendario>),
    });

  return h('.mac-form-row', [
    h('.label', DIAS[dia.diaSemana] ?? ''),
    h('.control.hstack.gap-2', [
      dia.trabalha
        ? h('.hstack.gap-1', [
            campoHora(dia.inicio, 'inicio'),
            h('span.t-secondary', 'às'),
            campoHora(dia.fim, 'fim'),
            h('span.t-secondary', { style: { marginLeft: '8px' } }, 'almoço'),
            campoHora(dia.pausaInicio ?? '12:00', 'pausaInicio'),
            campoHora(dia.pausaFim ?? '13:00', 'pausaFim'),
          ])
        : h('span.t-secondary', 'Sem produção'),
      botaoToggle(dia.trabalha, () => void gravar({ trabalha: !dia.trabalha })),
    ]),
  ]);
}

async function novoSetor(app: Aplicacao): Promise<void> {
  const nome = await perguntar({
    titulo: 'Novo setor',
    mensagem: 'Um setor é uma etapa do fluxo: onde o trabalho começa, é produzido, é acabado ou sai. Depois de criar, escolha o PAPEL dele na lista.',
    rotuloCampo: 'Nome',
    obrigatorio: true,
  });
  if (!nome) return;
  const id = nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  await app.repo.salvarSetor({
    id: `setor-${id || 'novo'}-${Date.now().toString(36)}`,
    nome,
    ordem: 99,
    cor: 'var(--sys-gray)',
    papel: 'outro',
    ativo: true,
  });
  notificar({ titulo: 'Setor criado', corpo: nome, icone: 'plus-circle' });
  app.redesenhar();
}

async function novaEstacao(app: Aplicacao): Promise<void> {
  const c = await app.repo.carregarCadastros();
  const nome = await perguntar({
    titulo: 'Nova estação',
    mensagem: 'Uma estação é uma máquina ou um posto que recebe etapas e tem fila própria.',
    rotuloCampo: 'Nome',
    obrigatorio: true,
  });
  if (!nome) return;
  const setorId = await perguntar({
    titulo: 'Em que setor?',
    rotuloCampo: 'Setor',
    opcoes: c.setores.map((s) => ({ valor: s.id, rotulo: s.nome })),
    obrigatorio: true,
  });
  if (!setorId) return;

  await app.repo.salvarEstacao({
    id: `est-${nome.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now().toString(36)}`,
    setorId,
    nome,
    codigo: nome.slice(0, 6).toUpperCase(),
    capacidadeParalela: 1,
    ativa: true,
  });
  notificar({ titulo: 'Estação criada', corpo: nome, icone: 'plus-circle' });
  app.redesenhar();
}

async function novoMotivo(app: Aplicacao): Promise<void> {
  const descricao = await perguntar({
    titulo: 'Novo motivo de pausa',
    rotuloCampo: 'Descrição',
    mensagem: 'Escreva como o operador fala. "Falta de papel" é melhor que "indisponibilidade de insumo".',
    obrigatorio: true,
  });
  if (!descricao) return;
  await app.repo.salvarMotivo({
    id: `motivo-${Date.now().toString(36)}`,
    codigo: descricao.slice(0, 8).toUpperCase().replace(/\s/g, ''),
    descricao,
    categoria: 'outro',
    exigeTexto: false,
    ativo: true,
  });
  app.redesenhar();
}

/* ================================================================== */
/* Pessoas                                                             */
/* ================================================================== */

export async function telaPessoas(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const c = await app.repo.carregarCadastros();
  const pinsDePartida = new Set(PINS_DE_PARTIDA.map((p) => p.pessoaId));
  const comPinDePartida = c.pessoas.filter((p) => pinsDePartida.has(p.id) && p.pinHash && p.pinHash.length <= 6);

  render(destino, [
    h('.vstack.gap-4.pcp-coluna-leitura', [
      comPinDePartida.length > 0
        ? aviso(
            'aviso',
            `${comPinDePartida.length} ${comPinDePartida.length === 1 ? 'pessoa está' : 'pessoas estão'} com o PIN que veio de fábrica. Troque antes de colocar o tablet no produção — o PIN é o que identifica quem apontou.`,
          )
        : null,

      h('.cartao', [
        tituloDeSecao(
          'Pessoas',
          botao('Nova pessoa', () => novaPessoa(app), { icone: 'plus', pequeno: true }),
        ),
        h('table.tab', [
          h('thead', h('tr', [
            h('th', 'Nome'),
            h('th', { style: { width: '130px' } }, 'Perfil'),
            h('th', 'Setores em que aponta'),
            h('th', { style: { width: '96px' } }, 'PIN'),
            h('th', { style: { width: '76px' } }, 'Ativa'),
          ])),
          h('tbody',
            c.pessoas.map((p) =>
              h('tr', [
                h('td', { style: { fontWeight: '500' } }, p.nome),
                h('td', h('select.mac-popup', {
                  onchange: async (ev: Event) => {
                    await app.repo.salvarPessoa({ ...p, perfil: (ev.target as HTMLSelectElement).value as Pessoa['perfil'] });
                    app.redesenhar();
                  },
                }, PERFIS.map((v) => h('option', { value: v.id, selected: v.id === p.perfil }, v.rotulo)))),
                h('td.t-callout.t-secondary',
                  p.setores.length === 0
                    ? 'Não aponta na produção'
                    : p.setores.map((id) => c.setores.find((s) => s.id === id)?.nome ?? '?').join(', ')),
                h('td', botao(p.pinHash ? 'Trocar' : 'Definir', () => definirPin(app, p), { pequeno: true })),
                h('td', botaoToggle(p.ativa, async () => {
                  await app.repo.salvarPessoa({ ...p, ativa: !p.ativa });
                  app.redesenhar();
                })),
              ]),
            ),
          ),
        ]),
      ]),

      h('.cartao', [
        h('.bloco-t', 'Como o operador entra no tablet'),
        h('.t-callout.t-secondary',
          'O operador não usa e-mail nem senha: toca o próprio nome e digita o PIN — é o que funciona com a mão suja de tinta, e é o que carimba cada apontamento com quem fez. O tablet, esse sim, é pareado uma vez na instalação com a conta do aparelho; depois disso ninguém digita senha nele.'),
      ]),
    ]),
  ]);
}

const PERFIS = [
  { id: 'operador', rotulo: 'Operador' },
  { id: 'encarregado', rotulo: 'Encarregado' },
  { id: 'pcp', rotulo: 'PCP' },
  { id: 'expedicao', rotulo: 'Expedição' },
  { id: 'vendedor', rotulo: 'Vendedor' },
  { id: 'direcao', rotulo: 'Direção' },
  { id: 'administrador', rotulo: 'Administrador' },
] as const;

async function novaPessoa(app: Aplicacao): Promise<void> {
  const nome = await perguntar({ titulo: 'Nova pessoa', rotuloCampo: 'Nome', obrigatorio: true });
  if (!nome) return;
  const perfil = await perguntar({
    titulo: 'Qual é o papel?',
    rotuloCampo: 'Perfil',
    opcoes: PERFIS.map((p) => ({ valor: p.id, rotulo: p.rotulo })),
    obrigatorio: true,
  });
  if (!perfil) return;

  await app.repo.salvarPessoa({
    id: `pessoa-${Date.now().toString(36)}`,
    nome,
    perfil: perfil as Pessoa['perfil'],
    pinHash: null,
    setores: [],
    usuarioId: null,
    ativa: true,
  });
  app.redesenhar();
}

async function definirPin(app: Aplicacao, p: Pessoa): Promise<void> {
  const pin = await perguntar({
    titulo: `PIN de ${p.nome}`,
    mensagem: 'Quatro a seis dígitos. É o que a pessoa digita no tablet para apontar.',
    rotuloCampo: 'PIN',
    obrigatorio: true,
  });
  if (!pin) return;
  if (!/^\d{4,6}$/.test(pin)) {
    notificar({ titulo: 'PIN inválido', corpo: 'Use de quatro a seis dígitos.', icone: 'exclamationmark-triangle' });
    return;
  }
  await app.repo.salvarPessoa({ ...p, pinHash: pin });
  notificar({ titulo: 'PIN definido', corpo: p.nome, icone: 'lock-fill' });
  app.redesenhar();
}

/* ================================================================== */
/* Aparelhos                                                           */
/* ================================================================== */

export async function telaAparelhos(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const dispositivos = await app.repo.dispositivos();
  const c = await app.repo.carregarCadastros();

  render(destino, [
    h('.vstack.gap-4.pcp-coluna-leitura', [
      h('.cartao', [
        tituloDeSecao('Tablets do produção'),
        dispositivos.length === 0
          ? vazio(
              'iphone',
              'Nenhum tablet registrado',
              'Abra o aplicativo do produção no tablet, escolha o setor e a estação. Ele aparece aqui com o último envio e a fila pendente.',
            )
          : h('table.tab', [
              h('thead', h('tr', [
                h('th', 'Aparelho'),
                h('th', { style: { width: '140px' } }, 'Setor'),
                h('th', { style: { width: '150px' } }, 'Último envio'),
                h('th', { style: { width: '100px' } }, 'Pendentes'),
                h('th', { style: { width: '90px' } }, 'Ativo'),
              ])),
              h('tbody',
                dispositivos.map((d) =>
                  h('tr', [
                    h('td', { style: { fontWeight: '500' } }, d.nome),
                    h('td.t-secondary', c.setores.find((s) => s.id === d.setorId)?.nome ?? '—'),
                    h('td.t-callout', d.ultimoSyncEm ? formato.dataHoraCurta(d.ultimoSyncEm) : 'nunca'),
                    h('td.num', d.pendentes > 0
                      ? h('span', { style: { color: 'var(--sys-orange)', fontWeight: '600' } }, String(d.pendentes))
                      : '0'),
                    h('td', botaoToggle(d.ativo, async () => {
                      await app.repo.salvarDispositivo({ ...d, ativo: !d.ativo });
                      app.redesenhar();
                    })),
                  ]),
                ),
              ),
            ]),
      ]),

      h('.cartao', [
        h('.bloco-t', 'O agente do computador do sistema da gráfica'),
        h('.vstack.gap-2', [
          h('.t-callout.t-secondary',
            'É o programa que vigia a pasta onde o sistema atual salva o PDF da OS e envia para cá. Enquanto ele não estiver instalado, a entrada de OS é arrastar os arquivos na tela de Importações.'),
          h('.pcp-ficha', [
            h('div', [h('dt', 'Estado'), h('dd', 'Não instalado')]),
            h('div', [h('dt', 'Pasta vigiada'), h('dd', 'a definir na instalação')]),
            h('div', [h('dt', 'Instalador'), h('dd', 'pcp.centriu.com/agente/')]),
          ]),
        ]),
      ]),

    ]),
  ]);
}

/* ================================================================== */
/* Parâmetros                                                          */
/* ================================================================== */

export async function telaParametros(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const c = await app.repo.carregarCadastros();
  const p = c.parametros;

  const gravar = async (chave: keyof Parametros, valor: unknown): Promise<void> => {
    await app.repo.salvarParametros({ ...p, [chave]: valor } as Parametros);
    app.redesenhar();
  };

  render(destino, [
    h('.vstack.gap-4.pcp-coluna-leitura.estreita', [
      aviso(
        'info',
        'Nenhum número aqui é verdade sobre a Gráfica Boa União: são pontos de partida para o sistema funcionar no primeiro dia. Ajuste conforme a operação mostrar o que é real.',
      ),
      h('.cartao', [
        h('.mac-form',
          DESCRICAO_PARAMETROS.map((d) =>
            h('.mac-form-row.tall', [
              h('.label.vstack', { style: { gap: '1px' } }, [
                h('span', d.titulo),
                h('span.t-subhead.t-secondary', { style: { maxWidth: '46ch' } }, d.ajuda),
              ]),
              h('.control', controle(d, p, gravar)),
            ]),
          ),
        ),
      ]),
      h('.cartao', [
        h('.bloco-t', 'Aparência'),
        h('.mac-form', [
          h('.mac-form-row', [
            h('.label', 'Tema'),
            h('.control', h('.mac-segmented', [
              h('button', { 'data-pref': 'theme', 'data-value': 'light' }, 'Claro'),
              h('button', { 'data-pref': 'theme', 'data-value': 'dark' }, 'Escuro'),
              h('button', { 'data-pref': 'theme', 'data-value': 'auto' }, 'Sistema'),
            ])),
          ]),
          h('.mac-form-row', [
            h('.label', 'Cor de destaque'),
            h('.control', h('.mac-swatches',
              ['blue', 'purple', 'pink', 'red', 'orange', 'yellow', 'green', 'graphite'].map((cor) =>
                h('button.mac-swatch-dot', {
                  style: { '--c': `var(--accent-${cor})` },
                  'data-pref': 'accent',
                  'data-value': cor,
                  'data-tooltip': cor,
                }),
              ),
            )),
          ]),
        ]),
      ]),
    ]),
  ]);
}

function controle(
  d: (typeof DESCRICAO_PARAMETROS)[number],
  p: Parametros,
  gravar: (chave: keyof Parametros, valor: unknown) => Promise<void>,
): HTMLElement {
  const valor = p[d.chave];

  if (d.tipo === 'booleano') {
    return botaoToggle(Boolean(valor), () => void gravar(d.chave, !valor));
  }
  if (d.tipo === 'opcao') {
    return h('select.mac-popup', {
      onchange: (ev: Event) => void gravar(d.chave, (ev.target as HTMLSelectElement).value),
    }, (d.opcoes ?? []).map((o) => h('option', { value: o.valor, selected: o.valor === valor }, o.rotulo)));
  }
  return h('.hstack.gap-2', [
    h('input.campo-e', {
      type: 'number',
      value: String(valor),
      min: d.min !== undefined ? String(d.min) : undefined,
      max: d.max !== undefined ? String(d.max) : undefined,
      style: { width: '92px' },
      onchange: (ev: Event) => void gravar(d.chave, Number((ev.target as HTMLInputElement).value)),
    }),
    d.unidade ? h('span.t-secondary', d.unidade) : null,
  ]);
}

/* ------------------------------------------------------------------ */

function botaoToggle(ligado: boolean, aoAlternar: () => void): HTMLElement {
  return h('button.mac-toggle', {
    type: 'button',
    role: 'switch',
    'aria-checked': ligado ? 'true' : 'false',
    onclick: (ev: MouseEvent) => {
      ev.preventDefault();
      aoAlternar();
    },
  });
}

export { sf };
